import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatCurrency, formatDateTime } from "@/lib/format";

type Expense = components["schemas"]["ExpenseResponse"];
type ExpenseState = components["schemas"]["ExpenseState"];

// The Record type makes this exhaustive: a new ExpenseState is a compile error.
export const EXPENSE_STATE_LABELS: Record<ExpenseState, string> = {
  Captured: "Captured",
  Coded: "Coded",
  PendingReview: "Pending review",
  Posting: "Posting",
  Posted: "Posted",
  Flagged: "Flagged",
  PostFailed: "Post failed",
};

export function expenseStateVariant(
  state: ExpenseState | undefined,
): "default" | "secondary" | "outline" | "destructive" {
  switch (state) {
    case "Posted":
      return "default";
    case "Coded":
    case "Posting":
      return "secondary";
    case "Flagged":
    case "PostFailed":
      return "destructive";
    default:
      return "outline";
  }
}

export interface ExpensesTableFilters {
  states?: ExpenseState[];
  merchant?: string;
  from?: string;
  to?: string;
}

interface ExpensesTableProps {
  organisationId?: string;
  filters?: ExpensesTableFilters;
}

const BASE_COLUMNS = 7;

export function ExpensesTable({ organisationId, filters }: ExpensesTableProps) {
  const showOrganisation = !organisationId;
  const columnCount = BASE_COLUMNS + (showOrganisation ? 1 : 0);

  const pagination = useCursorPagination({ initialLimit: 20 });

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const states = filters?.states?.join(",");
  const { merchant, from, to } = filters ?? {};

  const listKey = [organisationId, states, merchant, from, to].join("|");
  const prevListKeyRef = useRef(listKey);
  useEffect(() => {
    if (prevListKeyRef.current !== listKey) {
      prevListKeyRef.current = listKey;
      pagination.reset();
    }
  }, [listKey, pagination.reset]);

  useEffect(() => {
    let cancelled = false;

    async function fetchExpenses() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/expenses", {
        params: {
          query: {
            OrganisationId: organisationId,
            States: states ? (states.split(",") as ExpenseState[]) : undefined,
            Merchant: merchant,
            From: from,
            To: to,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load expenses.");
      } else {
        setExpenses(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchExpenses();
    return () => {
      cancelled = true;
    };
  }, [organisationId, states, merchant, from, to, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-2 flex h-5 items-center text-sm text-muted-foreground">
        {loading && <Spinner className="size-4" />}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Merchant</TableHead>
              <TableHead>Spender</TableHead>
              {showOrganisation && <TableHead>Organisation</TableHead>}
              <TableHead>State</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Spent</TableHead>
              <TableHead>Receipt</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {expenses.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="text-muted-foreground h-24 text-center">
                  No expenses found.
                </TableCell>
              </TableRow>
            ) : (
              expenses.map((expense) => (
                <TableRow key={expense.id}>
                  <TableCell>
                    <Link to={`/expenses/${expense.id}`}>
                      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                        {expense.id?.slice(0, 8)}...
                      </Badge>
                    </Link>
                  </TableCell>
                  <TableCell className="max-w-64">
                    <Link to={`/expenses/${expense.id}`} className="block truncate hover:underline">
                      {expense.merchant?.displayName || expense.merchantName || (
                        <span className="text-muted-foreground">Unknown merchant</span>
                      )}
                    </Link>
                    {expense.merchant?.displayName && expense.merchantName && (
                      <span className="block truncate font-mono text-xs text-muted-foreground">
                        {expense.merchantName}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {expense.spender?.userId ? (
                      <Link to={`/users/${expense.spender.userId}`} className="hover:underline">
                        {expense.spender.name || expense.spender.email}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  {showOrganisation && (
                    <TableCell>
                      <Link to={`/organisations/${expense.organisationId}`}>
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {expense.organisationId?.slice(0, 8)}...
                        </Badge>
                      </Link>
                    </TableCell>
                  )}
                  <TableCell>
                    {expense.state && (
                      <Badge variant={expenseStateVariant(expense.state)}>
                        {EXPENSE_STATE_LABELS[expense.state]}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(expense.amount, expense.currency)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {formatDateTime(expense.transactionDate)}
                  </TableCell>
                  <TableCell>
                    {expense.needsReceipt ? (
                      <Badge variant="outline">Missing</Badge>
                    ) : (
                      <Badge variant="secondary">Attached</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
